import { useState, useEffect } from 'react';
import {
  Network, Plus, Trash2, ChevronRight, ChevronDown, Info, RefreshCw, AlertCircle
} from 'lucide-react';
import useSiteSetupStore from '../../../stores/siteSetupStore';
import fetchWithTenant from '../../../utils/fetchWithTenant';
import { useBranding } from '../../../contexts/BrandingContext';

const CIDR_RE = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\/\d{1,2}$/;

const deriveCidrFromIp = (ip) => {
  if (!ip || typeof ip !== 'string') return '';
  const parts = ip.split('.');
  if (parts.length !== 4) return '';
  return `${parts[0]}.${parts[1]}.${parts[2]}.0/24`;
};

const NetworkConfigStep = ({ onNext }) => {
  const { savedData, markStepComplete } = useSiteSetupStore();
  const { branding } = useBranding();
  const brandName = branding.brandName;

  const [agent, setAgent] = useState(null);
  const [scanConfig, setScanConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [networks, setNetworks] = useState(['']);
  const [autoDetected, setAutoDetected] = useState(false);

  const [snmpOpen, setSnmpOpen] = useState(false);
  const [snmp, setSnmp] = useState({
    community: 'public',
    version: 'v2c',
    v3Username: '',
    v3AuthProtocol: 'SHA',
    v3AuthPassword: '',
    v3PrivProtocol: 'AES',
    v3PrivPassword: '',
  });

  const [wmiOpen, setWmiOpen] = useState(false);
  const [wmi, setWmi] = useState({ domain: '', username: '', password: '' });

  useEffect(() => { loadAgentAndConfig(); }, []);

  const loadAgentAndConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithTenant('/api/md/agents');
      if (!res.ok) throw new Error('Failed to load agents');
      const data = await res.json();
      const agents = data.agents || data || [];
      const wantId = savedData?.agentId;
      const found =
        (wantId && agents.find(a => a.id === wantId)) ||
        agents.find(a => a.status === 'ONLINE' || a.status === 'CONNECTED') ||
        agents[0];

      if (!found) {
        setError('No agent found. Please go back and complete agent setup.');
        setLoading(false);
        return;
      }
      setAgent(found);

      const cfgRes = await fetchWithTenant(`/api/md/agents/${found.id}/scan-config`);
      let cfg = {};
      if (cfgRes.ok) {
        const cfgData = await cfgRes.json();
        cfg = cfgData.scanConfig || cfgData || {};
      }
      setScanConfig(cfg);

      const existing = Array.isArray(cfg.defaultNetworks) ? cfg.defaultNetworks : [];
      if (existing.length > 0) {
        setNetworks(existing);
        setAutoDetected(false);
      } else {
        const guess = deriveCidrFromIp(found.localIp);
        setNetworks([guess || '']);
        setAutoDetected(!!guess);
      }

      if (cfg.snmp) setSnmp(s => ({ ...s, ...cfg.snmp }));
      if (cfg.wmi) setWmi(w => ({ ...w, ...cfg.wmi }));
    } catch (err) {
      console.error('[NetworkConfig] load failed:', err);
      setError(err.message || 'Failed to load agent details');
    } finally {
      setLoading(false);
    }
  };

  const updateNetwork = (idx, value) => {
    const next = [...networks];
    next[idx] = value.trim();
    setNetworks(next);
    setAutoDetected(false);
  };

  const addNetwork = () => setNetworks([...networks, '']);
  const removeNetwork = (idx) => {
    if (networks.length === 1) { setNetworks(['']); return; }
    setNetworks(networks.filter((_, i) => i !== idx));
  };

  const validNetworks = networks.map(n => n.trim()).filter(n => n && CIDR_RE.test(n));
  const canContinue = validNetworks.length > 0 && agent && !saving;

  const handleNext = async () => {
    if (!canContinue) return;
    setSaving(true);
    setError(null);
    try {
      const merged = {
        ...(scanConfig || {}),
        defaultNetworks: validNetworks,
        snmp: snmpOpen ? snmp : (scanConfig?.snmp || undefined),
        wmi: wmiOpen ? wmi : (scanConfig?.wmi || undefined),
      };
      const res = await fetchWithTenant(`/api/md/agents/${agent.id}/scan-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scanConfig: merged }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || 'Failed to save scan config');
      }
      await markStepComplete('network_config', {
        agentId: agent.id,
        networks: validNetworks,
        snmpConfigured: snmpOpen,
        wmiConfigured: wmiOpen,
      });
      onNext();
    } catch (err) {
      console.error('[NetworkConfig] save failed:', err);
      setError(err.message || 'Failed to save scan configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="site-setup-step">
        <div className="verify-loading">
          <RefreshCw size={24} className="spin" />
          <p>Loading network details...</p>
        </div>
      </div>
    );
  }

  const isWindowsAgent = agent?.osType?.toLowerCase().includes('win');

  return (
    <div className="site-setup-step network-config-step">
      <h3>Network Settings</h3>
      <p className="step-description">
        Confirm the subnets {brandName} should scan to discover devices on your network.
      </p>

      {error && (
        <div className="pairing-code-error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <div className="setup-form-section">
        <h4><Network size={14} /> Subnets to scan (CIDR)</h4>
        {autoDetected && (
          <div className="setup-hint-row">
            <Info size={13} />
            <span>Auto-detected from your agent ({agent?.localIp}). Edit if needed.</span>
          </div>
        )}
        <div className="cidr-list">
          {networks.map((n, i) => {
            const invalid = n.trim() && !CIDR_RE.test(n.trim());
            return (
              <div key={i} className="cidr-row">
                <input
                  type="text"
                  className={invalid ? 'error' : ''}
                  placeholder="192.168.1.0/24"
                  value={n}
                  onChange={(e) => updateNetwork(i, e.target.value)}
                />
                <button
                  type="button"
                  className="site-setup-btn outline small"
                  onClick={() => removeNetwork(i)}
                  title="Remove"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
          <button type="button" className="site-setup-btn outline small" onClick={addNetwork}>
            <Plus size={14} /> Add subnet
          </button>
        </div>
      </div>

      <div className="setup-form-section">
        <button
          type="button"
          className="collapsible-header"
          onClick={() => setSnmpOpen(o => !o)}
        >
          {snmpOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span>Advanced — SNMP credentials (optional)</span>
        </button>
        {snmpOpen && (
          <div className="collapsible-body">
            <p className="setup-hint">
              SNMP gives richer device details and switch port mapping. Skip if you don't have managed switches.
            </p>
            <div className="setup-form-row">
              <div className="setup-form-field">
                <label>Community string</label>
                <input
                  value={snmp.community}
                  onChange={(e) => setSnmp({ ...snmp, community: e.target.value })}
                  placeholder="public"
                />
              </div>
              <div className="setup-form-field">
                <label>Version</label>
                <select
                  value={snmp.version}
                  onChange={(e) => setSnmp({ ...snmp, version: e.target.value })}
                >
                  <option value="v1">v1</option>
                  <option value="v2c">v2c</option>
                  <option value="v3">v3</option>
                </select>
              </div>
            </div>
            {snmp.version === 'v3' && (
              <>
                <div className="setup-form-row">
                  <div className="setup-form-field">
                    <label>v3 Username</label>
                    <input
                      value={snmp.v3Username}
                      onChange={(e) => setSnmp({ ...snmp, v3Username: e.target.value })}
                    />
                  </div>
                  <div className="setup-form-field">
                    <label>Auth protocol</label>
                    <select
                      value={snmp.v3AuthProtocol}
                      onChange={(e) => setSnmp({ ...snmp, v3AuthProtocol: e.target.value })}
                    >
                      <option value="MD5">MD5</option>
                      <option value="SHA">SHA</option>
                    </select>
                  </div>
                </div>
                <div className="setup-form-row">
                  <div className="setup-form-field">
                    <label>Auth password</label>
                    <input
                      type="password"
                      value={snmp.v3AuthPassword}
                      onChange={(e) => setSnmp({ ...snmp, v3AuthPassword: e.target.value })}
                    />
                  </div>
                  <div className="setup-form-field">
                    <label>Privacy protocol</label>
                    <select
                      value={snmp.v3PrivProtocol}
                      onChange={(e) => setSnmp({ ...snmp, v3PrivProtocol: e.target.value })}
                    >
                      <option value="DES">DES</option>
                      <option value="AES">AES</option>
                    </select>
                  </div>
                </div>
                <div className="setup-form-row">
                  <div className="setup-form-field full-width">
                    <label>Privacy password</label>
                    <input
                      type="password"
                      value={snmp.v3PrivPassword}
                      onChange={(e) => setSnmp({ ...snmp, v3PrivPassword: e.target.value })}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {isWindowsAgent && (
        <div className="setup-form-section">
          <button
            type="button"
            className="collapsible-header"
            onClick={() => setWmiOpen(o => !o)}
          >
            {wmiOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span>Advanced — WMI credentials (Windows hosts, optional)</span>
          </button>
          {wmiOpen && (
            <div className="collapsible-body">
              <p className="setup-hint">
                WMI fingerprinting requires the AllowUnencrypted GPO on target Windows hosts.
              </p>
              <div className="setup-form-row">
                <div className="setup-form-field">
                  <label>Domain / workgroup</label>
                  <input
                    value={wmi.domain}
                    onChange={(e) => setWmi({ ...wmi, domain: e.target.value })}
                  />
                </div>
                <div className="setup-form-field">
                  <label>Username</label>
                  <input
                    value={wmi.username}
                    onChange={(e) => setWmi({ ...wmi, username: e.target.value })}
                  />
                </div>
              </div>
              <div className="setup-form-row">
                <div className="setup-form-field full-width">
                  <label>Password</label>
                  <input
                    type="password"
                    value={wmi.password}
                    onChange={(e) => setWmi({ ...wmi, password: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="step-actions">
        <button
          className="site-setup-btn primary"
          onClick={handleNext}
          disabled={!canContinue}
        >
          {saving ? 'Saving...' : 'Save & Continue'} <ChevronRight size={16} />
        </button>
        {validNetworks.length === 0 && (
          <span className="step-hint">Add at least one valid CIDR (e.g. 192.168.1.0/24)</span>
        )}
      </div>
    </div>
  );
};

export default NetworkConfigStep;
